/**
 * Overlapping Images Section
 * Repositions text elements outside their parent items for proper z-index stacking
 */

class OverlappingImagesSection {
  constructor(section) {
    this.section = section;
    this.container = section.querySelector('.overlapping-images__container');
    this.items = section.querySelectorAll('.overlapping-images__item');
    this.mobileBreakpoint = 750;
    this.isRepositioned = false;

    // Store original parent references for each text element
    this.textElements = [];
    this.items.forEach((item, index) => {
      const text = item.querySelector('.overlapping-images__text');
      if (text) {
        this.textElements.push({
          element: text,
          originalParent: item,
          itemIndex: index
        });
      }
    });

    // Ensure videos have playsinline for iOS and wait for metadata
    this.videos = section.querySelectorAll('.overlapping-images__image video');
    this.videos.forEach(video => {
      video.setAttribute('playsinline', '');
    });

    this.init();
  }

  init() {
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.debounce(this.handleResize, 100));

    // Wait for video metadata before initial positioning so dimensions are available
    const pendingVideos = Array.from(this.videos).filter(v => v.readyState < 1);
    if (pendingVideos.length > 0) {
      let loaded = 0;
      const onReady = () => {
        loaded++;
        if (loaded >= pendingVideos.length) {
          this.handleResize();
        }
      };
      pendingVideos.forEach(v => v.addEventListener('loadedmetadata', onReady, { once: true }));
      // Fallback: run anyway after 3s in case metadata is slow
      setTimeout(() => this.handleResize(), 3000);
    } else {
      this.handleResize();
    }
  }

  handleResize() {
    const isLandscapeWide = document.body.classList.contains('landscape-wide');
    const isMobile = window.innerWidth < this.mobileBreakpoint && !isLandscapeWide;
    const isStackMode = this.section.classList.contains('overlapping-images--stack-mobile');

    if (isMobile && isStackMode) {
      this.revertToOriginal();
    } else {
      this.repositionText();
    }
  }

  repositionText() {
    if (this.textElements.length === 0) return;

    // Reset padding before measuring to avoid circular dependency
    this.container.style.paddingTop = '';

    // Read offset values from data attributes (percentage of image height)
    const offsets = [
      parseFloat(this.section.dataset.offsetTextLeft) || 0,
      parseFloat(this.section.dataset.offsetTextRight) || 0
    ];

    // First pass: calculate positions and find if we need top padding
    const positions = [];
    let minTop = 0;

    this.textElements.forEach(({ element, originalParent, itemIndex }) => {
      const item = this.items[itemIndex];
      const image = item.querySelector('.overlapping-images__image');

      if (!image) {
        positions.push(null);
        return;
      }

      // Move text to container if not already there (needed to measure properly)
      if (element.parentElement !== this.container) {
        this.container.appendChild(element);
      }

      // Get image position relative to container
      const containerRect = this.container.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();

      // Calculate position
      const imageTop = imageRect.top - containerRect.top;
      const imageBottom = imageTop + imageRect.height;
      const imageLeft = imageRect.left - containerRect.left;
      const imageHeight = imageRect.height;

      const offsetPercent = offsets[itemIndex] || 0;
      const textHeight = element.offsetHeight;

      let textTop;
      if (offsetPercent >= 0) {
        const offsetPixels = (offsetPercent / 100) * imageHeight;
        textTop = imageBottom + offsetPixels;
      } else {
        const offsetPixels = (offsetPercent / 100) * (imageHeight + textHeight);
        textTop = imageBottom + offsetPixels;
      }

      positions.push({ textTop, imageLeft });

      // Track minimum top position
      if (textTop < minTop) {
        minTop = textTop;
      }
    });

    // Add padding-top to container if text extends above
    const paddingTop = minTop < 0 ? Math.abs(minTop) : 0;
    this.container.style.paddingTop = paddingTop > 0 ? `${paddingTop}px` : '';

    // Second pass: apply positions, adjusted for padding
    this.textElements.forEach(({ element }, index) => {
      const pos = positions[index];
      if (!pos) return;

      element.style.position = 'absolute';
      element.style.left = '0';  // Start at container edge, let CSS margin-left control position
      element.style.top = `${pos.textTop + paddingTop}px`;
      element.style.marginTop = '0';
      element.style.zIndex = '10';
    });

    this.isRepositioned = true;

    // Ensure container can accommodate absolutely positioned text at bottom
    this.adjustContainerHeight();
  }

  adjustContainerHeight() {
    // Find the lowest point of any text element
    let maxBottom = 0;

    this.textElements.forEach(({ element }) => {
      const rect = element.getBoundingClientRect();
      const containerRect = this.container.getBoundingClientRect();
      const bottom = rect.bottom - containerRect.top;
      if (bottom > maxBottom) {
        maxBottom = bottom;
      }
    });

    // Find the lowest point of any image
    this.items.forEach(item => {
      const image = item.querySelector('.overlapping-images__image');
      if (image) {
        const rect = image.getBoundingClientRect();
        const containerRect = this.container.getBoundingClientRect();
        const bottom = rect.bottom - containerRect.top;
        if (bottom > maxBottom) {
          maxBottom = bottom;
        }
      }
    });

    // Set min-height to accommodate all content
    this.container.style.minHeight = `${maxBottom}px`;
  }

  revertToOriginal() {
    if (!this.isRepositioned) return;

    this.textElements.forEach(({ element, originalParent }) => {
      // Move text back to original parent
      if (element.parentElement !== originalParent) {
        // Find the image in the parent to insert after
        const image = originalParent.querySelector('.overlapping-images__image');
        if (image) {
          // Check if text should be before or after image based on text-above class
          if (originalParent.classList.contains('overlapping-images__item--text-above')) {
            originalParent.insertBefore(element, image);
          } else {
            image.after(element);
          }
        } else {
          originalParent.appendChild(element);
        }
      }

      // Reset inline styles
      element.style.position = '';
      element.style.left = '';
      element.style.top = '';
      element.style.marginTop = '';  // Restore CSS margin-top for flex layout
      element.style.zIndex = '';
    });

    // Reset container adjustments
    this.container.style.minHeight = '';
    this.container.style.paddingTop = '';

    this.isRepositioned = false;
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

// Initialize all overlapping images sections
function initOverlappingImages() {
  document.querySelectorAll('.overlapping-images').forEach(section => {
    new OverlappingImagesSection(section);
  });
}

// Run on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initOverlappingImages);
} else {
  initOverlappingImages();
}

// Re-initialize on Shopify section load (for theme editor)
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('.overlapping-images');
  if (section) {
    new OverlappingImagesSection(section);
  }
});
